import { RequestService } from '../RequestService'
import { useEnvStore } from 'store'

export enum CollectType {
  /** 添加 */
  Add = 1,
  /** 删除 */
  Remove = 2,
}

export enum SymbolType {
  /** 币币 */
  Spot = 2,
}

export type GetHistoryKlineReq = {
  symbol: string
  resolution: string | number
  from: number
  to: number
}

/** 1 普通， 2 立即取消 */
export enum TrustType {
  Normal = 1,
  ImmediatelyCancel = 2,
}

/** 1 币币 | 2 杠杆 */
export enum AssetType {
  Spot = 1,
  Lever = 2,
}

export enum AssetTypeMap {
  Digital = 1,
  Fiat = 2,
}

/** 1 限价 | 2 市价 */
export enum OrderType {
  /** 限价 */
  Limit = 1,
  /** 市价 */
  Market = 2,
}

type OrderBuyOrSellReq = {
  trustType: TrustType
  assetType: AssetType
  type: OrderType
  symbolId: SymbolId
  /** 市价单 买入的金额 */
  total?: string
  /** 买入的价格, 限价单必须传入 */
  price?: string
  /** 买入的数量 - 限价单必须传 */
  num?: string
}

export interface OrderBuyReq extends OrderBuyOrSellReq {
  side: 'buy'
}

export interface OrderSellReq extends OrderBuyOrSellReq {
  side: 'sell'
}

class AppRequest extends RequestService {
  constructor() {
    super()
  }

  setBaseUrl() {
    const envStore = useEnvStore()

    return envStore.config?.app || ''
  }

  /**
   * 获取语言包
   * @param params
   * @returns
   */
  getLanguage(data: { lang: string }) {
    return this.post<Record<string, Record<string, string>>>('/Lang/WebLangPackage', data)
  }

  /**
   * 获取初始化信息
   * @returns
   */
  getWebInitInfo() {
    return this.post<ApiResponse.WebInitInfo>('/Publics/getWebInitInfo')
  }
  /**
   * 获取自选列表
   * @returns
   */
  getCollectList() {
    return this.post<CollectData>('/Common/getOptionList')
  }

  /**
   * 添加自选
   * @param data
   * @returns
   */
  addCollect(data: { symbolId: SymbolId; swapType: SymbolType }) {
    return this.post('/Common/addOption', {
      ...data,
      option: CollectType.Add,
    })
  }

  /**
   * 删除自选
   * @param data
   * @returns
   */
  removeCollect(data: { symbolId: SymbolId; swapType: SymbolType }) {
    return this.post('/Common/addOption', {
      ...data,
      option: CollectType.Remove,
    })
  }

  /**
   * 获取币种信息
   * @param data
   * @returns
   */
  getCoinInfo(data: { swapSymbolId: SymbolId }) {
    return this.post<ApiResponse.CoinInfo>('/App/Product/getCoinInfo', data)
  }

  /**
   * 获取历史K线数据
   * @param data
   * @returns
   */
  getHistoryKline(data: GetHistoryKlineReq) {
    return this.post<SocketResponse.HistoryKlineResponse>('/Lever/kline', data)
  }

  /**
   * 获取Kline精度
   * @param data
   * @returns
   */
  getSymbolInfo(data: { symbol: SymbolId }) {
    return this.post<ApiResponse.LeverSymbolInfo>('/Lever/symbols', data)
  }

  /**
   * 获取资产列表
   * @returns
   */
  getAssetsList(data: { type: string }) {
    return this.post<ApiResponse.AssetsData>('/AssetsV2/getAssetsV3', data)
  }

  /** 委托下单买入 */
  spotOrderBuy(data: OrderBuyReq) {
    return this.post('/Spot/Trade/trustStore', data)
  }

  /** 委托下单卖出 */
  spotOrderSell(data: OrderSellReq) {
    return this.post('/Spot/Trade/trustStore', data)
  }

  /**
   * 获取币币订单列表
   * @param data
   * @returns
   */
  getSpotOrderList(data = {}) {
    return this.post<ApiResponse.SpotOrderItem[]>('/Spot/Trade/trustIndex', data)
  }

  /**
   * 撤单
   * @param data
   * @returns
   */
  cancelOrder(data: { trustId: ApiResponse.SpotOrderItem['trustId'] }) {
    return this.post('/Spot/Trade/trustCancel', data)
  }

  /**
   * 获取历史订单列表
   * @param data
   * @returns
   */
  getHistoryOrderList(data: { page: number; pageSize: number; status?: string; symbolId?: SymbolId | string; side?: 'buy' | 'sell' | '' }) {
    if (!data.symbolId) {
      data.symbolId = ''
    }
    return this.post<HasPageInfoResponse<ApiResponse.SpotOrderItem>>('/Spot/Trade/trustDealIndex', data)
  }

  /**
   * 获取三方KYC信息
   * @returns
   */
  thirdKycInfo() {
    return this.post<ApiResponse.ThirdKycInfo[]>('/UCenter/Kyc/kycIntroductionv2')
  }

  getAppInitialData() {
    return this.post<ApiResponse.AppInitialData>('/V4/Common/Publics/appInitialData')
  }

  getHotActivities() {
    return this.post('/Api/Api/hotActivities')
  }

  /**
   * 撤销计划单
   * @param data
   * @returns
   */
  revokePlan(data: { planOrderId: number | string }) {
    return this.post('/Margin/revokePlan', data)
  }

  // 获取邀请链接
  getInviteUrl() {
    return this.post<{ inviteUrl: string }>('/Publics/getInviteUrl')
  }

  // 获取推广中心下面的数据
  getUserAssetsDepositStats() {
    return this.post<ApiResponse.InviteStats>('/v5/assetsdeposit/userAssetsDepositStats')
  }

  // 获取返佣统计的数据
  getUserRebateStats(data: { cubdCreatedAt: string }) {
    return this.post<ApiResponse.RebateStats>('/v5/assetsdeposit/userRebateStats', data)
  }

  // 推广中心-好友列表接口
  getFriendRebateStats = (data: { pageNum: number; pageSize: number }) => {
    return this.post<ApiResponse.InviteUserListResponse>('/v5/assetsdeposit/getFriendRebateStats', data)
  }
}
/** 应用服务接口 */
export const AppServices = new AppRequest()
